import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Layout, Card, Table, Tag, Button, Space, Typography, Segmented,
  Modal, Input, message, Tooltip, Spin, Empty, Rate, Checkbox, Upload, Select,
} from 'antd';
import {
  ReloadOutlined, WhatsAppOutlined, CheckCircleOutlined,
  CloseCircleOutlined, SendOutlined, UploadOutlined, DeleteOutlined, FileOutlined,
} from '@ant-design/icons';

const { Content } = Layout;
const { Text, Title } = Typography;

const API = '/api/v1/jj';
const h = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`, 'Content-Type': 'application/json' });

export default function JJRenewalsPage() {
  const { t } = useTranslation();
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [windowFilter, setWindowFilter] = useState('all');
  const [updating, setUpdating] = useState(null); // contract id being updated
  const [intentModal, setIntentModal] = useState(null); // { id, intent }
  const [intentNote, setIntentNote] = useState('');

  // Move-in state
  const [moveInModal, setMoveInModal] = useState(null); // contract id
  const [moveInChecklist, setMoveInChecklist] = useState({
    keys_handed_over: false,
    utilities_transferred: false,
    inventory_checked: false,
  });
  const [moveInScore, setMoveInScore] = useState(0);
  const [moveInNotes, setMoveInNotes] = useState('');

  // Document state
  const [docModal, setDocModal] = useState(null); // contract id
  const [documents, setDocuments] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const loadRenewals = useCallback(async () => {
    setLoading(true);
    try {
      const win = windowFilter !== 'all' ? `&window=${windowFilter}` : '';
      const res = await fetch(`${API}/contracts/renewals?${win}`, { headers: h() }).then(r => r.json());
      setRenewals(res.renewals || []);
    } catch (e) {
      message.error(t('common.loadFailed', 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [windowFilter, t]);

  useEffect(() => { loadRenewals(); }, [loadRenewals]);

  const handleUpdateIntent = async () => {
    if (!intentModal) return;
    setUpdating(intentModal.id);
    try {
      const res = await fetch(`${API}/contracts/${intentModal.id}/renewal-intent`, {
        method: 'PATCH', headers: h(),
        body: JSON.stringify({ renewal_intent: intentModal.intent, note: intentNote }),
      });
      const data = await res.json();
      if (data.ok) {
        message.success(t('renewals.intentUpdated', 'Renewal intent updated'));
        setIntentModal(null);
        setIntentNote('');
        loadRenewals();
      }
    } catch (e) {
      message.error(t('renewals.intentUpdateFailed', 'Failed to update'));
    } finally {
      setUpdating(null);
    }
  };

  const handleSendRenewalMessage = (contract) => {
    const customer = contract.customer || {};
    const name = customer.name || 'there';
    const listing = contract.listing_title || 'the property';
    const msg = `Hi ${name}! 😊\n\nYour lease for 🏠 ${listing} is expiring in ${contract.days_left} days.\n\nWould you like to renew? Let me know and I can help arrange everything!`;
    navigator.clipboard.writeText(msg);
    message.success(t('renewals.renewalMsgCopied', 'Renewal message copied! Paste into Line / WhatsApp'));
  };

  const handleMoveIn = async () => {
    if (!moveInModal) return;
    setUpdating(moveInModal);
    try {
      const res = await fetch(`${API}/contracts/${moveInModal}/move-in`, {
        method: 'PATCH', headers: h(),
        body: JSON.stringify({
          checklist: moveInChecklist,
          satisfaction_score: moveInScore,
          notes: moveInNotes,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        message.success(t('moveIn.confirmed', 'Move-in confirmed!'));
        setMoveInModal(null);
        setMoveInChecklist({ keys_handed_over: false, utilities_transferred: false, inventory_checked: false });
        setMoveInScore(0);
        setMoveInNotes('');
        loadRenewals();
      }
    } catch (e) {
      message.error(t('moveIn.failed', 'Failed to confirm move-in'));
    } finally {
      setUpdating(null);
    }
  };

  // ── Document Management ──
  const loadDocuments = async (contractId) => {
    setDocLoading(true);
    try {
      const res = await fetch(`${API}/contracts/${contractId}/documents`, { headers: h() }).then(r => r.json());
      setDocuments(res.documents || []);
    } catch { /* silent */ }
    finally { setDocLoading(false); }
  };

  const handleUploadDocument = async (contractId, file, docType) => {
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docType);
      const token = localStorage.getItem('spo_auth_token');
      const res = await fetch(`${API}/contracts/${contractId}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (data.ok) {
        message.success(t('docs.uploaded', 'Document uploaded'));
        loadDocuments(contractId);
      }
    } catch (e) {
      message.error(t('docs.uploadFailed', 'Upload failed'));
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (contractId, docId) => {
    try {
      await fetch(`${API}/contracts/${contractId}/documents/${docId}`, {
        method: 'DELETE', headers: h(),
      });
      message.success(t('docs.deleted', 'Document deleted'));
      loadDocuments(contractId);
    } catch (e) {
      message.error(t('docs.deleteFailed', 'Delete failed'));
    }
  };

  const getDaysColor = (days) => {
    if (days == null) return 'default';
    if (days <= 14) return 'red';
    if (days <= 30) return 'orange';
    if (days <= 60) return 'gold';
    return 'default';
  };

  const columns = [
    {
      title: t('renewals.customer', 'Customer'),
      dataIndex: ['customer', 'name'],
      key: 'customer',
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name || t('common.unknown', 'Unknown')}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.customer?.line_id ? `💬 ${record.customer.line_id}` : ''}
            {record.customer?.whatsapp ? ` 📱 ${record.customer.whatsapp}` : ''}
            {record.customer?.phone ? ` ☎ ${record.customer.phone}` : ''}
          </Text>
        </Space>
      ),
    },
    {
      title: t('renewals.property', 'Property'),
      dataIndex: 'listing_title',
      key: 'property',
      render: (title, record) => (
        <div>
          <Text>{title || '-'}</Text>
          {record.rent_price && (
            <div><Text type="secondary" style={{ fontSize: 11 }}>฿{record.rent_price.toLocaleString()}/month</Text></div>
          )}
        </div>
      ),
    },
    {
      title: t('renewals.endDate', 'Lease End'),
      dataIndex: 'rent_end_date',
      key: 'endDate',
      render: (date) => date ? new Date(date).toLocaleDateString('zh-TW') : '-',
    },
    {
      title: t('renewals.daysLeft', 'Days Left'),
      dataIndex: 'days_left',
      key: 'daysLeft',
      sorter: (a, b) => (a.days_left || 9999) - (b.days_left || 9999),
      defaultSortOrder: 'ascend',
      render: (days) => (
        <Tag color={getDaysColor(days)} style={{ fontWeight: 600 }}>
          {days != null ? `${days} ${t('renewals.days', 'days')}` : '-'}
        </Tag>
      ),
    },
    {
      title: t('renewals.intent', 'Renewal Intent'),
      dataIndex: 'renewal_intent',
      key: 'intent',
      render: (intent) => {
        if (intent === 'yes') return <Tag color="green" icon={<CheckCircleOutlined />}>{t('renewals.intentYes', 'Will Renew')}</Tag>;
        if (intent === 'no') return <Tag color="red" icon={<CloseCircleOutlined />}>{t('renewals.intentNo', 'Won\'t Renew')}</Tag>;
        return <Tag>{t('renewals.intentPending', 'Not Asked')}</Tag>;
      },
    },
    {
      title: t('common.actions', 'Actions'),
      key: 'actions',
      render: (_, record) => (
        <Space size={4} wrap>
          <Tooltip title={t('renewals.sendRenewalMsg', 'Send renewal message')}>
            <Button
              size="small"
              icon={<WhatsAppOutlined />}
              onClick={() => handleSendRenewalMessage(record)}
            >
              {t('renewals.msg', 'Message')}
            </Button>
          </Tooltip>
          {!record.renewal_intent && (
            <>
              <Tooltip title={t('renewals.intentYes', 'Will Renew')}>
                <Button
                  size="small"
                  type="primary"
                  style={{ background: '#52c41a', borderColor: '#52c41a' }}
                  icon={<CheckCircleOutlined />}
                  loading={updating === record.id}
                  onClick={() => { setIntentModal({ id: record.id, intent: 'yes' }); setIntentNote(''); }}
                >
                  {t('renewals.markYes', 'Yes')}
                </Button>
              </Tooltip>
              <Tooltip title={t('renewals.intentNo', 'Won\'t Renew')}>
                <Button
                  size="small"
                  danger
                  icon={<CloseCircleOutlined />}
                  loading={updating === record.id}
                  onClick={() => { setIntentModal({ id: record.id, intent: 'no' }); setIntentNote(''); }}
                >
                  {t('renewals.markNo', 'No')}
                </Button>
              </Tooltip>
            </>
          )}
          <Tooltip title={t('moveIn.confirmMoveIn', 'Confirm Move-in')}>
            <Button
              size="small"
              type="primary"
              ghost
              style={{ borderColor: '#722ed1', color: '#722ed1' }}
              onClick={() => {
                setMoveInModal(record.id);
                setMoveInChecklist({ keys_handed_over: false, utilities_transferred: false, inventory_checked: false });
                setMoveInScore(0);
                setMoveInNotes('');
              }}
            >
              🏠 {t('moveIn.moveInBtn', 'Move-in')}
            </Button>
          </Tooltip>
          <Tooltip title={t('docs.manageDocs', 'Manage Documents')}>
            <Button
              size="small"
              icon={<FileOutlined />}
              onClick={() => { setDocModal(record.id); loadDocuments(record.id); }}
            >
              {t('docs.title', 'Docs')}
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const totalCount = renewals.length;
  const criticalCount = renewals.filter(r => r.days_left != null && r.days_left <= 14).length;
  const warningCount = renewals.filter(r => r.days_left != null && r.days_left > 14 && r.days_left <= 30).length;

  return (
    <Content style={{ padding: 24, background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>🔄 {t('sidebar.jjRenewals', 'Renewals')}</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t('renewals.subtitle', 'Manage lease renewals and follow-ups')}
          </Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadRenewals}>{t('common.reload', 'Reload')}</Button>
      </div>

      {/* Stats summary */}
      {!loading && renewals.length > 0 && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <Card size="small" style={{ flex: 1, textAlign: 'center', background: '#fff' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#722ed1' }}>{totalCount}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{t('renewals.totalRenewals', 'Total Renewals')}</Text>
          </Card>
          <Card size="small" style={{ flex: 1, textAlign: 'center', background: '#fff1f0' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#ff4d4f' }}>{criticalCount}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{t('renewals.criticalLabel', '≤14 days')}</Text>
          </Card>
          <Card size="small" style={{ flex: 1, textAlign: 'center', background: '#fff7e6' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#fa8c16' }}>{warningCount}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{t('renewals.warningLabel', '15-30 days')}</Text>
          </Card>
        </div>
      )}

      {/* Window filter */}
      <div style={{ marginBottom: 16 }}>
        <Segmented
          value={windowFilter}
          onChange={setWindowFilter}
          options={[
            { label: t('renewals.filterAll', 'All'), value: 'all' },
            { label: t('renewals.filter60d', '≤60d'), value: '60d' },
            { label: t('renewals.filter30d', '≤30d'), value: '30d' },
            { label: t('renewals.filter14d', '≤14d'), value: '14d' },
          ]}
        />
      </div>

      {/* Renewals table */}
      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
        ) : renewals.length === 0 ? (
          <Empty description={t('renewals.noRenewals', 'No renewals found')} />
        ) : (
          <Table
            dataSource={renewals}
            columns={columns}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `${t('common.total', 'Total')}: ${total}` }}
          />
        )}
      </Card>

      {/* Intent note modal */}
      <Modal
        title={intentModal?.intent === 'yes'
          ? t('renewals.confirmYes', 'Confirm Renewal')
          : t('renewals.confirmNo', 'Confirm Non-Renewal')}
        open={!!intentModal}
        onOk={handleUpdateIntent}
        onCancel={() => { setIntentModal(null); setIntentNote(''); }}
        confirmLoading={!!updating}
        okText={t('common.confirm', 'Confirm')}
        cancelText={t('common.cancel', 'Cancel')}
        okButtonProps={{
          style: intentModal?.intent === 'yes'
            ? { background: '#52c41a', borderColor: '#52c41a' }
            : { background: '#ff4d4f', borderColor: '#ff4d4f' },
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            {intentModal?.intent === 'yes'
              ? t('renewals.confirmYesDesc', 'This will create a follow-up task to restart property search for this customer.')
              : t('renewals.confirmNoDesc', 'This will record that the customer does not wish to renew.')}
          </Text>
        </div>
        <Input.TextArea
          value={intentNote}
          onChange={e => setIntentNote(e.target.value)}
          placeholder={t('renewals.notePlaceholder', 'Optional note...')}
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      </Modal>

      {/* Move-in confirmation modal */}
      <Modal
        title={<span>🏠 {t('moveIn.confirmMoveIn', 'Move-in Confirmation')}</span>}
        open={!!moveInModal}
        onOk={handleMoveIn}
        onCancel={() => setMoveInModal(null)}
        confirmLoading={!!updating}
        okText={t('moveIn.confirmMoveIn', 'Confirm Move-in')}
        cancelText={t('common.cancel', 'Cancel')}
        okButtonProps={{ style: { background: '#722ed1', borderColor: '#722ed1' } }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('moveIn.checklist', 'Move-in Checklist')}</Text>
          <Checkbox
            checked={moveInChecklist.keys_handed_over}
            onChange={e => setMoveInChecklist(prev => ({ ...prev, keys_handed_over: e.target.checked }))}
          >
            {t('moveIn.keysHandedOver', 'Keys handed over')}
          </Checkbox>
          <br />
          <Checkbox
            checked={moveInChecklist.utilities_transferred}
            onChange={e => setMoveInChecklist(prev => ({ ...prev, utilities_transferred: e.target.checked }))}
          >
            {t('moveIn.utilitiesTransferred', 'Utilities transferred')}
          </Checkbox>
          <br />
          <Checkbox
            checked={moveInChecklist.inventory_checked}
            onChange={e => setMoveInChecklist(prev => ({ ...prev, inventory_checked: e.target.checked }))}
          >
            {t('moveIn.inventoryChecked', 'Inventory checked')}
          </Checkbox>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('moveIn.satisfaction', 'Satisfaction Score')}</Text>
          <Rate value={moveInScore} onChange={setMoveInScore} />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('moveIn.notes', 'Notes')}</Text>
          <Input.TextArea
            value={moveInNotes}
            onChange={e => setMoveInNotes(e.target.value)}
            placeholder={t('moveIn.notesPlaceholder', 'Any notes about the move-in...')}
            autoSize={{ minRows: 2, maxRows: 4 }}
          />
        </div>
      </Modal>

      {/* Document management modal */}
      <Modal
        title={<span>📄 {t('docs.manageDocs', 'Contract Documents')}</span>}
        open={!!docModal}
        onCancel={() => { setDocModal(null); setDocuments([]); }}
        footer={null}
        width={500}
      >
        <div style={{ marginBottom: 16, padding: '12px', background: '#fafafa', borderRadius: 8 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('docs.uploadNew', 'Upload New Document')}</Text>
          <Space>
            <Select
              size="small"
              defaultValue="other"
              style={{ width: 140 }}
              id="docTypeSelect"
              options={[
                { value: 'id_copy', label: t('docs.idCopy', 'ID Copy') },
                { value: 'deposit_receipt', label: t('docs.depositReceipt', 'Deposit Receipt') },
                { value: 'lease_agreement', label: t('docs.leaseAgreement', 'Lease Agreement') },
                { value: 'other', label: t('docs.other', 'Other') },
              ]}
            />
            <input
              type="file"
              id="docFileInput"
              onChange={(e) => {
                const file = e.target.files?.[0];
                const docType = document.getElementById('docTypeSelect')?.value || 'other';
                if (file && docModal) handleUploadDocument(docModal, file, docType);
              }}
              disabled={uploadingDoc}
            />
          </Space>
        </div>

        {docLoading ? (
          <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
        ) : documents.length === 0 ? (
          <Empty description={t('docs.noDocs', 'No documents')} />
        ) : (
          documents.map((doc) => (
            <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
              <div>
                <Text strong style={{ fontSize: 13 }}>{doc.file_name}</Text>
                <div>
                  <Tag>{doc.doc_type}</Tag>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}
                  </Text>
                </div>
              </div>
              <Space>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteDocument(docModal, doc.id)}
                />
              </Space>
            </div>
          ))
        )}
      </Modal>
    </Content>
  );
}
